<script setup lang="ts">import { toRef as _toRef } from 'vue';

import type {
  MultiplaceGift,
  MultiplaceGiftsContentVotes,
  PlaceDetail,
} from '@consumer/types/serializers'

import { isDesktop } from '@consumer/utils/breakpoints'

import { useUrlSearchParams, whenever } from '@vueuse/core'
import { every } from 'lodash-es'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import GEditableText from '@consumer/components/GEditableText.vue'
import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import { useSharedData } from '@consumer/composables/sharedData'
import { warnIfContainsCardWord } from '@consumer/utils/compliance'
import newBookOpenIcon from '@consumer/assets/icons/new/book-open.svg?svgicon'
import PlaceTypeGeneral from './GiftGuide/PlaceTypeGeneral.vue'
import PlaceTypeYelp from './GiftGuide/PlaceTypeYelp.vue'
import ContentVoting from './ContentVoting.vue'

withDefaults(defineProps<{
  gift: GiftTemplate
  places: PlaceDetail[]
  themeColor?: string
  logoColor?: string
  isEditing?: boolean
  multiPlaceGiftIdea?: MultiplaceGift
  disableEditing?: boolean
  editGiftTips?: boolean
  currentVotes?: MultiplaceGiftsContentVotes[]
  useWysiwygEditor?: boolean
}>(), { isEditing: false,editGiftTips: false,useWysiwygEditor: true, })

const emit = defineEmits<{
  startEditingField: []
  finishEditingField: []
  thumbsUpVote: []
  thumbsDownVote: []
  unvote: []
}>()

const urlParams = useUrlSearchParams()

const placesRefs = ref<{
  [key: string]: any
}>({})

const placesLoaded = ref<{
  [key: string]: boolean
}>({})

const __$temp_1 = (useSharedData()),
  currentUser = _toRef(__$temp_1, 'currentUser');
const canVote = currentUser.value?.canVoteOnContent

const blurbContentVoting = computed(
  () => __props.currentVotes?.find(contentVote => contentVote.field === 'gift_guide_blurb'))

const assignPlacesRef = (placeId: number, ref: any) => {
  placesRefs.value[placeId] = ref
  placesLoaded.value[placeId] = false
}

const goToPlace = (place: PlaceDetail) => {
  const placeRef = placesRefs.value[place.giftlyId!]
  if (placeRef) {
    placeRef.scrollIntoView({
      alignToTop: true,
      behavior: 'smooth',
    })
  }
}

whenever(
  () => every(placesLoaded.value, Boolean),
  () => {
    if (urlParams.placeId) {
      const place = __props.gift.places?.find(place => place.giftlyId?.toString() === urlParams.placeId)
      if (place) {
        nextTick(() => {
          goToPlace(place)
          delete urlParams.placeId
        })
      }
    }
  },
)

const iconColor = computed(() => {
  return __props.logoColor?.toUpperCase() === '#FFFFFF' ? __props.themeColor : __props.logoColor
})

async function blurbChanged (giftGuideBlurb: string) {
  await warnIfContainsCardWord(giftGuideBlurb)
  await MultiPlaceGiftIdeasApi.update({ params: __props.multiPlaceGiftIdea, data: { giftGuideBlurb } })
  emit('finishEditingField')
}

async function aiOptionClicked () {
  return MultiPlaceGiftIdeasApi.guidebookRecommendation(__props.multiPlaceGiftIdea)
}

defineExpose({
  goToPlace,
})
</script>

<template>
  <div class="py-4 space-y-4 bg-white rounded-2xl shadow-md" data-testid="gift-guide">
    <div class="bg-white">
      <div class="px-6 pb-4 pt-0 lg:pt-4 text-xs space-y-4">
        <div class="flex flex-col items-center gap-6 pt-6 lg:pt-0">
          <div
            class="bg-white rounded-1 shadow-xl w-76px h-76px flex items-center justify-center"
            :style="{ filter: 'drop-shadow(0px 3px 8px rgba(0, 0, 0, 0.10))' }"
          >
            <GIcon
              :svg="newBookOpenIcon"
              size="44px"
              class="!stroke-4 !fill-4"
              :style="{ color: iconColor }"
            />
          </div>

          <h1 class="text-2xl lg:text-3xl font-semibold">
            Giftly Guidebook
          </h1>

          <div class="text-center font-medium text-xl w-full gift-guide-description" :style="{ textWrap: 'pretty' }">
            <ContentVoting
              v-if="!isEditing && canVote"
              :contentVote="blurbContentVoting"
              @thumbsUpVote="emit('thumbsUpVote')"
              @thumbsDownVote="emit('thumbsDownVote')"
              @voteRemoved="emit('unvote')"
            >
              <template #content>
                <span data-testid="gift-guide-blurb" v-html="gift.giftlyMetadata?.giftGuideBlurb"/>
              </template>
            </ContentVoting>
            <GEditableText
              v-else-if="isEditing && multiPlaceGiftIdea?.giftlyMetadata"
              v-model="multiPlaceGiftIdea.giftlyMetadata.giftGuideBlurb"
              multiline
              :aiOptionCallback="aiOptionClicked"
              :disabled="disableEditing"
              :useWysiwygEditor="useWysiwygEditor"
              :onSaveChanges="blurbChanged"
              @finishEditing="emit('finishEditingField')"
              @startEditing="emit('startEditingField')"
            />
            <span
              v-else
              data-testid="gift-guide-blurb"
              v-html="gift.giftlyMetadata?.giftGuideBlurb"
            />
          </div>
        </div>
      </div>
    </div>

    <template
      v-for="place, i in places"
      :key="place.id"
    >
      <div
        :ref="el => assignPlacesRef(place.giftlyId!, el)"
        :data-placeid="place.giftlyId"
        :style="{ scrollMarginTop: isDesktop ? '80px' : '60px' }"
      >
        <PlaceTypeYelp
          v-if="place.placeType === 'yelp' || place.placeType === 'list' || place.placeType === 'brand'"
          :gift="gift"
          :place="place"
          :multiPlaceGiftIdea="multiPlaceGiftIdea"
          :index="i + 1"
          :total="places.length"
          class="border-t-10 border-solid"
          :style="{ borderColor: `${iconColor}30` }"
          :editGiftTips="editGiftTips"
          @loaded="placesLoaded[place.giftlyId!] = true"
        />
        <PlaceTypeGeneral
          v-else
          :gift="gift"
          :place="place"
          :index="i + 1"
          :total="places.length"
          class="border-t-10 border-solid"
          :style="{ borderColor: `${iconColor}30` }"
          @loaded="placesLoaded[place.giftlyId!] = true"
        />
      </div>
    </template>

  </div>
</template>
